import React, { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import Auth from '../utils/auth';
import { Card } from 'bootstrap';
import { GET_ME } from '../utils/queries';
import { DELETE_POST, RANK_SOLUTION } from '../utils/mutations';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import Button from 'react-bootstrap/Button';
import Post from './Post';
import { Tooltip } from 'react-tooltip';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Paper from '@mui/material/Paper';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import ModeStandbyIcon from '@mui/icons-material/ModeStandby';
import { Text } from '@visx/text';
import { scaleLog } from '@visx/scale';
import Wordcloud from '@visx/wordcloud/lib/Wordcloud';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import WarningIcon from '@mui/icons-material/Warning';
import ArticleIcon from '@mui/icons-material/Article';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import LinkIcon from '@mui/icons-material/Link';
import EmailIcon from '@mui/icons-material/Email';
import InfoIcon from '@mui/icons-material/Info';
import QRCode from 'react-qr-code';
import QrShareActions from './QrShareActions';
import Comments from './Comments';
import External from './External';
import UserBadge from './UserBadge';
import { removeStopwords } from 'stopword';
import { findAb604District } from '../utils/ab604';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, ChartTooltip, Legend);

const WordcloudBuilder = (props) => {
  const colors = ["#E7E7E7", "#BFBFBF", "#8E8E8E"];

  const fontScale = scaleLog({
    domain: [
      Math.min(...props.words.map(w => w.value)),
      Math.max(...props.words.map(w => w.value))
    ],
    range: [10, 26]
  });

  const fontSizeSetter = datum => fontScale(datum.value);
  const fixedValueGenerator = () => 0.5;

  return (
    <div className="pc-wordcloud">
      <Wordcloud
        key={'wordcloud-' + props.keyProp}
        words={props.words}
        width={props.width}
        height={props.height}
        fontSize={fontSizeSetter}
        font={"Impact"}
        padding={1}
        rotate={0}
        random={fixedValueGenerator}
      >
        {cloudWords =>
          cloudWords.map((w, i) => (
            <a
              key={`wc-link-${props.keyProp}-${w.text}`}
              className="pc-wordcloud__link"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                alert(w.text);
              }}
            >
              <Text
                key={w.text}
                fill={colors[i % colors.length]}
                textAnchor={"middle"}
                transform={`translate(${w.x}, ${w.y})`}
                fontSize={w.size}
                fontFamily={w.font}
              >
                {w.text}
              </Text>
            </a>
          ))
        }
      </Wordcloud>
    </div>
  );
};

const normalizeRankedVotes = (rankedVotes) => {
  if (!Array.isArray(rankedVotes)) return [];
  return rankedVotes.filter((vote) => Number.isInteger(Number(vote?.rank)) && Number(vote.rank) > 0);
};

const getRankVoteCounts = (solution) => {
  const rankedVotes = normalizeRankedVotes(solution?.rankedVotes);
  const counts = rankedVotes.reduce(
    (accumulator, vote) => {
      const rank = Number(vote.rank);
      if (rank === 1) accumulator.firstChoiceVotes += 1;
      if (rank === 2) accumulator.secondChoiceVotes += 1;
      if (rank === 3) accumulator.thirdChoiceVotes += 1;
      return accumulator;
    },
    {
      firstChoiceVotes: 0,
      secondChoiceVotes: 0,
      thirdChoiceVotes: 0,
      rankedBallots: rankedVotes.length,
    }
  );

  return counts;
};

const computeRankedChoiceScore = (solution, solutionCount) => {
  const rankedVotes = normalizeRankedVotes(solution?.rankedVotes);
  if (rankedVotes.length > 0) {
    return rankedVotes.reduce((sum, vote) => {
      const rank = Number(vote.rank);
      const points = Math.max(0, solutionCount - rank + 1);
      return sum + points;
    }, 0);
  }

  const upVoteCount = Array.isArray(solution?.upVotes) ? solution.upVotes.length : 0;
  const downVoteCount = Array.isArray(solution?.downVotes) ? solution.downVotes.length : 0;
  return upVoteCount - downVoteCount;
};

const applyRankVoteForUser = (solutions, { solutionId, userId, rank }) => {
  const userIdString = String(userId || '');
  const targetSolutionId = String(solutionId || '');
  const normalizedRank = Number.isInteger(Number(rank)) && Number(rank) > 0 ? Number(rank) : null;

  return (Array.isArray(solutions) ? solutions : []).map((solution) => {
    const solutionIdString = String(solution?._id || '');
    const currentVotes = normalizeRankedVotes(solution?.rankedVotes);

    const nextVotes = currentVotes.filter((vote) => {
      if (String(vote?.userId || '') !== userIdString) return true;
      if (solutionIdString === targetSolutionId) return false;
      if (normalizedRank != null && Number(vote.rank) === normalizedRank) return false;
      return true;
    });

    if (solutionIdString === targetSolutionId && normalizedRank != null) {
      nextVotes.push({ userId: userIdString, rank: normalizedRank });
    }

    return {
      ...solution,
      rankedVotes: nextVotes,
    };
  });
};

const IndividualSolution = (props) => {
  const rankedVotes = useMemo(
    () => normalizeRankedVotes(props.solution?.rankedVotes),
    [props.solution?.rankedVotes]
  );
  const currentUserId = props.currentUser ? String(props.currentUser) : '';
  const myVote = rankedVotes.find((vote) => String(vote.userId || '') === currentUserId);
  const myRank = myVote ? Number(myVote.rank) : '';
  const rankVoteCounts = getRankVoteCounts(props.solution);
  const solutionScore =
    Number.isFinite(props.solutionScore)
      ? props.solutionScore
      : computeRankedChoiceScore(props.solution, props.solutionCount);
  const overallRank = Number.isFinite(props.overallRank) ? props.overallRank : null;
  const rankTier = Number.isFinite(props.rankTier) ? props.rankTier : 4;

  function createMarkup(text) {
    const urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/ig;
    const htmlText = text.replace(urlRegex, function (url) {
      return `<a href="${url}" target="_blank" class="block max-w-full truncate" rel="noreferrer">${url}</a>`;
    });
    return { __html: htmlText };
  }

  const colorSpan = (net) => {
    if (net > 0) return <div className="vote-count vote-count--pos">{net}</div>;
    if (net < 0) return <div className="vote-count vote-count--neg">{net}</div>;
    return <div className="vote-count vote-count--zero">{net}</div>;
  };

  const useText = createMarkup(props.solution.solution);
  function confirmExternalNavigation(e) {
    const anchor = e.target.closest("a");
    if (!anchor) return;

    const href = anchor.getAttribute("href");
    if (!href || href.startsWith("#")) return;

    e.preventDefault();

    const confirmed = window.confirm(
      `You are about to leave this website and open an external link.\n\n🔗${href}\n\nDo not continue if you do not recognize the link.`
    );

    if (confirmed) {
      window.open(href, "_blank", "noopener,noreferrer");
    }
  }
  return (
    <div
      key={`solution-${props.solution._id}`}
      className={`solutionRow solutionRow--tier-${rankTier}`}
    >
      <div className={`voteBox voteBox--tier-${rankTier}`}>
        <div className="solutionNet">{colorSpan(solutionScore)}</div>
        <div className={`rank-chip rank-chip--tier-${rankTier}`}>
          {overallRank ? `Overall #${overallRank}` : 'Overall rank'}
        </div>
        <div className="rank-breakdown">
          <span>1st: {rankVoteCounts.firstChoiceVotes}</span>
          <span>2nd: {rankVoteCounts.secondChoiceVotes}</span>
          <span>3rd: {rankVoteCounts.thirdChoiceVotes}</span>
        </div>
        <div className="rank-ballots">Ballots: {rankVoteCounts.rankedBallots}</div>
        {Auth.loggedIn() && (
          <select
            className="mt-1 rounded border border-gray-300 bg-white px-1 py-[2px] text-[10px] text-gray-900"
            value={myRank}
            disabled={props.isSavingRank}
            onChange={(e) => {
              const nextValue = e.target.value;
              const rankValue = nextValue ? Number(nextValue) : null;
              props.onRankChange?.(props.solution._id, rankValue);
            }}
          >
            <option value="">No Rank</option>
            {Array.from({ length: props.solutionCount }, (_, index) => index + 1).map((rankOption) => (
              <option key={`${props.solution._id}-rank-${rankOption}`} value={rankOption}>
                Rank {rankOption}
              </option>
            ))}
          </select>
        )}
      </div>

      <div
        className="solutionText w-full min-w-0"
        onClick={confirmExternalNavigation}
        dangerouslySetInnerHTML={useText}
      />
    </div>
  );
};

const Solutions = (props) => {
  const [rankSolution] = useMutation(RANK_SOLUTION);
  const parsedSolutions = useMemo(() => {
    let solutions = props.solutions;
    try {
      solutions = JSON.parse(props.solutions);
    } catch (e) {
      solutions = props.solutions;
    }
    return Array.isArray(solutions) ? solutions : [];
  }, [props.solutions]);

  const [solutionState, setSolutionState] = useState(parsedSolutions);
  const [savingSolutionId, setSavingSolutionId] = useState(null);

  useEffect(() => {
    setSolutionState(parsedSolutions);
  }, [parsedSolutions]);

  const solutionMetricsById = useMemo(() => {
    const entries = solutionState.map((solution) => {
      const id = String(solution?._id || '');
      const score = computeRankedChoiceScore(solution, solutionState.length);
      const counts = getRankVoteCounts(solution);
      return {
        id,
        score,
        ...counts,
      };
    });

    const sorted = [...entries].sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (b.firstChoiceVotes !== a.firstChoiceVotes) return b.firstChoiceVotes - a.firstChoiceVotes;
      if (b.secondChoiceVotes !== a.secondChoiceVotes) return b.secondChoiceVotes - a.secondChoiceVotes;
      if (b.thirdChoiceVotes !== a.thirdChoiceVotes) return b.thirdChoiceVotes - a.thirdChoiceVotes;
      return a.id.localeCompare(b.id);
    });

    const rankById = {};
    sorted.forEach((entry, index) => {
      rankById[entry.id] = index + 1;
    });

    return entries.reduce((accumulator, entry) => {
      const overallRank = rankById[entry.id];
      accumulator[entry.id] = {
        ...entry,
        overallRank,
        rankTier: overallRank === 1 ? 1 : overallRank === 2 ? 2 : overallRank === 3 ? 3 : 4,
      };
      return accumulator;
    }, {});
  }, [solutionState]);

  const handleRankChange = useCallback(
    async (solutionId, rankValue) => {
      if (!Auth.loggedIn() || !props.currentUser) {
        return;
      }

      const maxRank = Math.max(solutionState.length, 1);
      const normalizedRank =
        Number.isInteger(Number(rankValue)) && Number(rankValue) > 0
          ? Math.min(Number(rankValue), maxRank)
          : null;

      setSavingSolutionId(String(solutionId));
      try {
        await rankSolution({
          variables: {
            postId: props.id,
            solutionId,
            rank: normalizedRank,
          },
        });

        setSolutionState((prev) =>
          applyRankVoteForUser(prev, {
            solutionId,
            userId: props.currentUser,
            rank: normalizedRank,
          })
        );
      } catch (error) {
        console.error('Failed to save ranked vote:', error);
        alert('Unable to save ranked vote.');
      } finally {
        setSavingSolutionId(null);
      }
    },
    [props.currentUser, props.id, rankSolution, solutionState.length]
  );

  if (solutionState.length > 0) {
    return (
      <div className="solutionsList">
        {solutionState.map((solution) => {
          const metrics = solutionMetricsById[String(solution?._id || '')] || {};
          return (
            <IndividualSolution
              solution={solution}
              key={`sol-${solution._id}`}
              id={props.id}
              currentUser={props.currentUser}
              solutionCount={solutionState.length}
              onRankChange={handleRankChange}
              isSavingRank={savingSolutionId === String(solution._id)}
              solutionScore={metrics.score}
              overallRank={metrics.overallRank}
              rankTier={metrics.rankTier}
            />
          );
        })}
      </div>
    );
  }
  return null;
};

// ... keep your Issues, PostInstance, SelectedPosts logic as-is ...


const Issues = (props) => {
    const issues = props.issues;
    const runIssueSearch = (rawTerm) => {
      const term = String(rawTerm || '').trim().toLowerCase();
      if (!term) return;

      props.filterData?.(term);
      props.setSearch?.(term);
      props.setSearchText?.(term);
      props.setSearchMade?.(true);
      props.setIsSelectedPaneOpen?.(false);
    };

    if (issues?.length > 0) {
      return (
        <Accordion slotProps={{ transition: { unmountOnExit: true } }} key={'accordion-issues-' + props.id}  className="w-full my-2 mx-auto justify-self-center text-xs">
        <AccordionSummary id="panel-header" aria-controls="panel-content">
        <div className="">
          <span key={"issues-" + props.id} className={"mx-auto"}>❗ Related Issues</span>
            <p>Browse issue categories and tap a chip to filter posts.</p>
        </div>
        </AccordionSummary>
              {issues.map((issue, index) => {
                  return (
                
                    <AccordionDetails>
                      <div key={"issue-" + props.id + '-' + index} className=" align-items-center justify-content-center text-center text-xs">
                          <div><button className="btn-active mx-1" onClick={() => runIssueSearch(issue.category)}>{issue.category}</button>
                          {issue.issues.map((issueTerm, index) => {
                              return (
                                  <button onClick={() => runIssueSearch(issueTerm)} key={"issue-" + index} style={{color: "#777"}} className="btn-active mx-1 mx-auto">
                                        {issueTerm}
                                    </button>
                              )}
                          )}
                          </div>
                      </div>  
                    </AccordionDetails>
                
                  );
              })}
          </Accordion>
      );
    } else {
      // console.log(issues);
      // console.log("No issues found.");
    }
}

const QR_SIZE = 160;

const PostInstance = (props) => {
    // console.log(props.post);
    const [isVisible, setIsVisible] = useState(false);
    const [firstLoad, setFirstLoad] = useState(true);
    const [isQrExpanded, setIsQrExpanded] = useState(false);
    const postRef = useRef(null);
    const qrCodeWrapperRef = useRef(null);
    const buildSolutionEmail = (solutions) => {
        let email = "\n\nI would like to propose the following solution(s) to address the issue(s) above:\n\n";
        try {
          solutions.forEach((solution) => {
              email += solution.solution + "\n\n";
          });
        } catch(e) {
        }
        email += "\n\nThank you for your time and consideration.\n\nSincerely,\n\n";
        console.log(email);
        return email;
    };

    function scaleFontSize(textLength) {
        // Clamp the length of the text between 0 and 128
        const clampedLength = Math.min(Math.max(textLength, 0), 128);
      
        // Calculate the font size based on the length of the text.
        // The formula used for scaling linearly is:
        // (y2 - y1) / (x2 - x1) * (x - x1) + y1
        // where (x1, y1) and (x2, y2) are the points (0, 72) and (128, 18)
        // representing the maximum and minimum font sizes at the corresponding text lengths.
        const fontSize = ((18 - 48) / 128) * clampedLength + 48;
      
        return `${fontSize}px`;
    }
    const onPostDeleted = async (deletedId, userId) => {
      const delKey = String(deletedId);

      // Snapshot previous state (for rollback)
      let prevSelectedSnapshot;
      let prevGeoSnapshot;

      // 1) Optimistically remove from selected posts
      props.setSelectedMapPosts?.((prev) => {
        prevSelectedSnapshot = prev; // capture
        return (prev || []).filter((p) => String(p?.properties?.id) !== delKey);
      });

      // 2) Optimistically remove from map geojson
      props.setPostGeoJSON?.((prev) => {
        prevGeoSnapshot = prev; // capture
        if (!prev) return prev;

        if (Array.isArray(prev)) {
          return prev.filter((f) => String(f?.properties?.id) !== delKey);
        }

        if (prev.type === "FeatureCollection" && Array.isArray(prev.features)) {
          return {
            ...prev,
            features: prev.features.filter((f) => String(f?.properties?.id) !== delKey),
          };
        }

        return prev;
      });

      // 3) Execute mutation (authoritative delete)
      try {
        console.log("Deleting post with ID:", deletedId);
        console.log(userId);  
        await deletePost({variables: { userId: postData.properties.userId, postId: id}});

      } catch (e) {
        console.log("Deleting post with ID:", deletedId);
        console.log(userId);
        console.error("Delete failed:", e);

        // Roll back optimistic UI
        if (props.setSelectedMapPosts) props.setSelectedMapPosts(prevSelectedSnapshot);
        if (props.setPostGeoJSON) props.setPostGeoJSON(prevGeoSnapshot);

        alert("Delete failed. Please try again.");
      }
    };

    const getZoomLevel = () => {
        try {
            if (props.viewport.zoom > 12.5) { 
                return props.viewport?.zoom;
            } else {
                return 12.5;
            }
        } catch (e) {
            return 12.5;
        }
    }
    const buildLocation = (civicInfo, ab604District) => {
      const city = civicInfo?.location ?? "";
      const state = civicInfo?.state ?? "";
      const district = civicInfo?.district ?? "";
      const isInAb604 = Boolean(ab604District);
      const districtText = isInAb604
        ? `AB-604 District ${ab604District}`
        : district
          ? `District ${district}`
          : "";
      const hasText = city || state || districtText;
      if (!hasText) return null;

      return (
        <div style={{ fontWeight: isInAb604 ? 700 : 400 }}>
          {city}{city && state ? ", " : ""}{state}
          {districtText ? ` (${districtText})` : ""}
        </div>
      );
    };

    const buildCivicInfo = (civicInfo, body, solutions) => {
      if (!civicInfo || civicInfo === "No civic info available.") {
        return <div>No civic information available yet.</div>;
      }

      // Group reps by "area" (US House, US Senate, Governor, etc.)
      const reps = Array.isArray(civicInfo?.representatives) ? civicInfo.representatives : [];
      if (reps.length === 0) return <div>No civic information available yet.</div>;

      const groups = reps.reduce((acc, rep) => {
        const key = rep?.area || "Other";
        (acc[key] ||= []).push(rep);
        return acc;
      }, {});

      const areaOrder = ["US House", "US Senate", "Governor", "SecState", "AttorneysGeneral", "Other"];
      const sortedAreas = Object.keys(groups).sort((a, b) => {
        const ai = areaOrder.indexOf(a);
        const bi = areaOrder.indexOf(b);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      });

      return sortedAreas.map((area, areaIdx) => {
        const officials = groups[area];

        return (
          <div key={`area-${area}`} elevation={1} className={"my-1"}>
            <div className={"py-2"}>
              <div>{area}</div>

              {officials.map((official, index) => (
                <div key={`official-${area}-${index}`}>
                  <div className={"my-1"}>{official?.name}</div>

                  {/* Optional: "reason" from 5calls */}
                  {official?.reason && (
                    <div style={{ fontSize: "12px", opacity: 0.85 }}>{official.reason}</div>
                  )}

                  {/* Main office phone */}
                  {official?.phone && (
                    <>
                      <a className="hotlink" href={`tel:${official.phone}`}>
                        Phone: {official.phone}
                      </a>
                      <br />
                    </>
                  )}

                  {/* Website */}
                  {official?.url && (
                    <>
                      <a className="hotlink" href={official.url} target="_blank" rel="noreferrer">
                        <LinkIcon /> Website
                      </a>
                      <br />
                    </>
                  )}

                  {/* Photo (optional) */}
                  {official?.photoURL && (
                    <div style={{ marginTop: 6, marginBottom: 6 }}>
                      <img
                        src={official.photoURL}
                        alt={official?.name ?? "Representative photo"}
                        style={{ width: 64, height: 64, borderRadius: 12, objectFit: "cover" }}
                        className="mx-auto"
                      />
                    </div>
                  )}

                  {/* Field offices list */}
                  {Array.isArray(official?.field_offices) && official.field_offices.length > 0 && (
                    <div style={{ fontSize: "12px", marginTop: 6 }}>
                      <div style={{ fontWeight: 600 }}>Field Offices</div>
                      {official.field_offices.map((fo, i) => (
                        <div key={`field-office-${official?.id ?? official?.name}-${i}`}>
                          {fo?.city ? `${fo.city}: ` : ""}
                          {fo?.phone ? (
                            <a className="hotlink" href={`tel:${fo.phone}`}>
                              {fo.phone}
                            </a>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Google-based links (kept similar to your current UX) */}
                  <a
                    className="hotlink"
                    href={"https://www.google.com/search?q=" + encodeURI(`${official?.name ?? ""} controversy`)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <WarningIcon />Check Controversy
                  </a>
                  <br />

                  {/* NOTE: 5calls reps endpoint does NOT include emails.
                      If you want email, you’ll need another data source or a different 5calls endpoint (if available). */}
                  {/* {official?.emails && ... } */}
                </div>
              ))}
            </div>

            {areaIdx < sortedAreas.length - 1 ? <hr /> : null}
          </div>
        );
      });
    };
    const [deletePost, { error, data }] = useMutation(DELETE_POST);
    const postData = props.post;
    const id = postData.properties.id;
    const user = postData.properties.user;
    let idme;
    if (typeof postData.properties.idme === 'string') {
      try {
        idme = JSON.parse(postData.properties.idme);
      } catch (error) {
        console.error("Error parsing idme:", error);
        idme = {};
      }
    } else {
      // If it's already an object, just assign it directly.
      idme = postData.properties.idme;
    }
    const body = postData.properties.body;
    const solutions = postData.properties.solutions;
    let issues = [];
    try {
      issues = JSON.parse(postData.properties.issues);
    } catch(e) {
      issues = postData.properties.issues;
    }
    let civicInfoObject = postData?.properties?.civicInfo;
    if (typeof civicInfoObject === 'string') {
        try {
            // Attempt to parse the string to an object
            civicInfoObject = JSON.parse(civicInfoObject);
        } catch (error) {
            // If parsing fails, log the error and set a default message
            // console.error("Failed to parse civic info:", error);
            civicInfoObject = "No civic info available.";
        }
    }
    
    // Ensure buildCivicInfo is called with the correctly parsed object or the default message
    let civicInfoData;
    if (civicInfoObject !== "No civic info available.") {
        // If parsing was successful, use the parsed object
        civicInfoData = buildCivicInfo(civicInfoObject, body, solutions);
    } else {
        // Handle the case where parsing was unsuccessful or not needed
        // Adjust this part according to how you want to handle the default/fallback scenario
        civicInfoData = civicInfoObject; // or any other default processing
    }
    const time = new Date(postData.properties.time).toLocaleString();
    const nearby = postData.properties.nearby;
    const rawLatitude = Number(postData.geometry.coordinates[1]);
    const rawLongitude = Number(postData.geometry.coordinates[0]);
    const latitude = rawLatitude.toFixed(5);
    const longitude = rawLongitude.toFixed(5);
    const ab604District = useMemo(() => {
      return findAb604District(rawLongitude, rawLatitude, props.ab604GeoJSON);
    }, [rawLongitude, rawLatitude, props.ab604GeoJSON]);
    const qrValue = useMemo(() => {
      try {
        const url = new URL(window.location.href);

      // force https
      url.protocol = 'https:';

      url.searchParams.set('postId', id);
      url.searchParams.set('forceURL', 'true');

      return url.toString();
    } catch (e) {
      return `https://${window.location.host}/?postId=${encodeURIComponent(id)}&forceURL=true`;
    }
  }, [id]);

    const downloadQrSvg = useCallback(() => {
      const svgElement = qrCodeWrapperRef.current?.querySelector('svg');
      if (!svgElement) {
        alert('QR code is not ready yet.');
        return;
    }

    const serializer = new XMLSerializer();
    let svgMarkup = serializer.serializeToString(svgElement);
    if (!svgMarkup.includes('xmlns="http://www.w3.org/2000/svg"')) {
      svgMarkup = svgMarkup.replace(
        '<svg',
        '<svg xmlns="http://www.w3.org/2000/svg"'
      );
    }

    const blob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' });
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `post-${id}-qr.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    }, [id]);

    const downloadQrPng = useCallback(() => {
      const svgElement = qrCodeWrapperRef.current?.querySelector('svg');
      if (!svgElement) {
        alert('QR code is not ready yet.');
        return;
      }

      const serializer = new XMLSerializer();
      let svgMarkup = serializer.serializeToString(svgElement);
      if (!svgMarkup.includes('xmlns="http://www.w3.org/2000/svg"')) {
        svgMarkup = svgMarkup.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
      }
      if (!svgMarkup.includes('xmlns:xlink="http://www.w3.org/1999/xlink"')) {
        svgMarkup = svgMarkup.replace('<svg', '<svg xmlns:xlink="http://www.w3.org/1999/xlink"');
      }

      const svgData = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`;
      const img = new Image();
      img.onload = () => {
        const scale = 4;
        const canvas = document.createElement('canvas');
        canvas.width = QR_SIZE * scale;
        canvas.height = QR_SIZE * scale;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.setTransform(scale, 0, 0, scale, 0, 0);
        ctx.drawImage(img, 0, 0, QR_SIZE, QR_SIZE);

        const pngUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = pngUrl;
        link.download = `post-${id}-qr.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };
      img.onerror = () => {
        alert('Failed to render QR code image.');
      };
      img.src = svgData;
    }, [id]);

    useEffect(() => {
      const observer = new IntersectionObserver(
          (entries) => {
              // The callback will return an array of entries, even if you are observing a single item
              const [entry] = entries;
              // Use the isIntersecting property to determine if the element is in the viewport
              if (entry.isIntersecting) {
                  setIsVisible(true);
                  observer.disconnect(); // Optional: disconnect the observer if you only want to load once
              }
          },
          {
              rootMargin: '100px', // Adjust rootMargin to load items slightly before they come into view
          }
      );

      const currentPostRef = postRef.current;
      if (currentPostRef) {
          observer.observe(currentPostRef);
      }

      return () => {
          if (currentPostRef) {
              observer.unobserve(currentPostRef);
          }
      };
    }, []);
    if (!isVisible) {
      
      // if (firstLoad && !isVisible) {
      //   setFirstLoad(false);
      //   return <div ref={postRef} className={"post drop-shadow-xl rounded-xl align-items-center justify-content-center text-center"}>Loading...</div>;
      // }
    }
    return (
    <>
    <div id={id} key={id} className={"post rounded-3xl  align-items-center justify-content-center text-center"}>
          <div width="100%" height="100%" id={id + "-text"}>
          <div className="break-words">
              <div elevation={2} className="rounded-3xl ">
                  <div className={'my-3 text-lg'}>
                    {body}
                  </div>
                  <div className="align-middle">
                    <UserBadge
                      idme={idme}
                      user={user}
                      iconColor={postData?.properties?.iconColor}
                    />
                  </div>       
              <div className="post-byline my-2">
              <button className="btn-active my-2" onClick={() => {
                  props.setViewport({latitude: postData.geometry.coordinates[1], longitude: postData.geometry.coordinates[0], zoom: getZoomLevel()});
                  props.setIsSelectedPaneOpen(false);
                  props.setIsVisiblePaneOpen(false);
                }}>🔍 View</button>
                {(props?.currentUser?.toString() === postData.properties.userId.toString()) && 
                    <>
                        <Button className="btn-active m-3" onClick={() => { 
                            props.setEditUser(postData.properties.userId.toString());
                            props.setEditPostId(id); 
                            props.setEditPostBody(body); 
                            props.setIsEditMode(true);
                            props.setIsPostPaneOpen(true);
                            props.setIsSelectedPaneOpen(false);
                        }} type="button">📝 Update</Button>
                        <Button
                          className="btn-active my-3"
                          onClick={async () => {
                            try {
                              // Tell the parent (map owner) to remove the feature from state.
                              onPostDeleted?.(id, postData.properties.userId);
                            } catch (e) {
                              console.error("Delete failed:", e);
                              alert("Delete failed. Please try again.");
                            }
                          }}
                          type="button"
                        >
                          ☠️ Delete
                        </Button>
                      </>
                    
               }<br/>Latitude, Longitude: {latitude}, {longitude}
              <br/>
              {buildLocation(civicInfoObject, ab604District)}
              {time}
              {nearby && <><br/><a
                  data-tooltip-id="nearby-tooltip"
                  data-tooltip-content="Nearby when posting."
                  data-tooltip-place="top"
                > <span className="material-symbols-outlined">
                how_to_reg
                </span></a><Tooltip id="nearby-tooltip" /></>}
                <br/>
                <span style={{fontSize: "10px"}} className="text-left">ID: {id}</span>
         <br/>
                </div>
               </div>
              
              <Accordion slotProps={{ transition: { unmountOnExit: true } }} key={'accordion-solutions-' + id} className="rounded-xs w-full my-2 mx-auto justify-self-center text-xs">
                    <AccordionSummary id="panel-header" aria-controls="panel-content" >
                    <div className="">
                      <span key={"government-" + id} className={"mx-auto"}>✨ Provided + Generated Solutions</span>
                        <p>View and vote on solutions using ranked choice voting.</p>
                    </div>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Solutions currentUser={props.currentUser} id={id} solutions={solutions} /> 
                    </AccordionDetails>
                </Accordion>
                <Accordion slotProps={{ transition: { unmountOnExit: true } }} key={'accordion-civic-' + id} className="rounded-xs w-full my-2 mx-auto justify-self-center text-xs">
                    <AccordionSummary id="panel-header" aria-controls="panel-content">
                    <div className="">
                      <span key={"government-" + id} className={"mx-auto"}>🗳️ Government Representatives</span>
                        <p>See officials tied to this location and their contact info.</p>
                    </div>
                    </AccordionSummary>
                    <AccordionDetails>
                      <div className="representative-window">
                        <i>Please be respectful.</i>
                        {civicInfoData}
                      </div>
                    </AccordionDetails>
                </Accordion>
                
                <Comments 
                    currentUser={props.currentUser}
                    id={id}
                    setIsLoginPaneOpen={props.setIsLoginPaneOpen}
                    setIsRegisterPaneOpen={props.setIsRegisterPaneOpen}
                />
                <Issues 
                  id={id}
                  currentUser={props.currentUser}
                  filterData={props.filterData}
                  setSearch={props.setSearch}
                  searchText={props.searchText}
                  setSearchText={props.setSearchText}
                  search={props.search}
                  searchMade={props.searchMade}
                  setSearchMade={props.setSearchMade}
                  setIsSelectedPaneOpen={props.setIsSelectedPaneOpen}
                  setViewport={props.setViewport}
                  issues={issues} 
                />
                
                <External
                  id={id}
                  latitude={latitude}
                  longitude={longitude}
                />
                <Accordion
                  expanded={isQrExpanded}
                  onChange={(_, expanded) => setIsQrExpanded(expanded)}
                  slotProps={{ transition: { unmountOnExit: true } }}
                  key={'accordion-qr-' + id}
                  className="rounded-xs w-full my-2 mx-auto justify-self-center text-xs"
                >
                  <AccordionSummary id="panel-header" aria-controls="panel-content">
                  <div className="">
                    <span key={"qr-" + id} className={"mx-auto"}>🌐 QR Code</span>
                      <p>Share this post via link or download the code.</p>
                  </div>
                  </AccordionSummary>
                  <AccordionDetails>
                      {isQrExpanded && (
                        <div className="w-full rounded-lg bg-gray-50 p-3">
                          <div className="flex justify-center">
                            <div ref={qrCodeWrapperRef} className="rounded-md bg-white p-2">
                              <QRCode value={qrValue} size={QR_SIZE} bgColor="#FFFFFF" fgColor="#000000" />
                            </div>
                          </div>
                          <QrShareActions
                            shareValue={qrValue}
                            linkText="Share this Post"
                            onDownloadSvg={downloadQrSvg}
                            onDownloadPng={downloadQrPng}
                          />
                        </div>
                      )}
                  </AccordionDetails>
                </Accordion>
          </div>
          </div>
      </div>
    </>)
};
const SelectedPosts = (props) => {
  const [wordsForCloud, setWordsForCloud] = useState([]);
  const [solutionWordsForCloud, setSolutionWordsForCloud] = useState([]);
  const [showWordCloud, setShowWordCloud] = useState(false);
  const [showIssueCategories, setShowIssueCategories] = useState(false);
  const [ab604GeoJSON, setAb604GeoJSON] = useState(null);
  const [activeIssueCategory, setActiveIssueCategory] = useState('');

  const parseIssues = useCallback((rawIssues) => {
    let issues = rawIssues;
    if (!issues) return [];

    if (typeof issues === 'string') {
      try {
        issues = JSON.parse(issues);
      } catch (e) {
        issues = [];
      }
    }

    if (!Array.isArray(issues)) {
      issues = [issues];
    }

    return issues;
  }, []);

  const issueCategoryCounts = useMemo(() => {
    if (!showIssueCategories) return [];

    const countsByKey = {};

    (props.selectedMapPosts || []).forEach((post) => {
      const issues = parseIssues(post?.properties?.issues);
      if (!issues.length) return;

      const seenInPost = new Set();

      issues.forEach((issue) => {
        let category = '';
        if (typeof issue === 'string') {
          category = issue;
        } else if (issue && typeof issue.category === 'string') {
          category = issue.category;
        }

        const trimmed = String(category || '').trim();
        if (!trimmed) return;

        const key = trimmed.toLowerCase();
        if (seenInPost.has(key)) return;
        seenInPost.add(key);

        if (!countsByKey[key]) {
          countsByKey[key] = { label: trimmed, count: 0 };
        }
        countsByKey[key].count += 1;
      });
    });

    return Object.values(countsByKey).sort((a, b) => b.count - a.count);
  }, [showIssueCategories, props.selectedMapPosts, parseIssues]);

  const filteredSelectedMapPosts = useMemo(() => {
    const activeKey = String(activeIssueCategory || '').trim().toLowerCase();
    if (!activeKey) return props.selectedMapPosts || [];

    return (props.selectedMapPosts || []).filter((post) => {
      const issues = parseIssues(post?.properties?.issues);
      return issues.some((issue) => {
        if (typeof issue === 'string') {
          return issue.trim().toLowerCase() === activeKey;
        }
        if (issue && typeof issue.category === 'string') {
          return issue.category.trim().toLowerCase() === activeKey;
        }
        return false;
      });
    });
  }, [props.selectedMapPosts, activeIssueCategory, parseIssues]);

  const issueCategoryChartData = useMemo(() => {
    if (!showIssueCategories) {
      return { labels: [], datasets: [] };
    }

    const topCategories = issueCategoryCounts.slice(0, 15);
    return {
      labels: topCategories.map((entry) => entry.label),
      datasets: [
        {
          label: 'Posts mentioning category',
          data: topCategories.map((entry) => entry.count),
          backgroundColor: topCategories.map((entry) => (
            entry.label.toLowerCase() === activeIssueCategory ? '#F3F4F6' : '#E5E7EB'
          )),
          borderWidth: 0
        }
      ]
    };
  }, [showIssueCategories, issueCategoryCounts, activeIssueCategory]);

  const activeIssueCategoryLabel = useMemo(() => {
    const match = issueCategoryCounts.find(
      (entry) => entry.label.toLowerCase() === activeIssueCategory
    );
    return match?.label || activeIssueCategory;
  }, [issueCategoryCounts, activeIssueCategory]);

  const issueCategoryChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: 'Most Discussed Issue Categories',
        color: '#fff'
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          color: '#fff',
          precision: 0
        }
      },
      y: {
        ticks: {
          color: '#fff'
        }
      }
    }
  }), []);

  const handleIssueBarClick = useCallback((event, elements) => {
    if (!elements?.length) return;
    const index = elements[0]?.index;
    const label = issueCategoryChartData?.labels?.[index];
    if (!label) return;
    const nextKey = label.toLowerCase();
    setActiveIssueCategory((prev) => (prev === nextKey ? '' : nextKey));
  }, [issueCategoryChartData]);

  useEffect(() => {
    let mounted = true;

    const loadAb604 = async () => {
      try {
        const response = await fetch('/AB-604.geojson');
        if (!response.ok) return;
        const data = await response.json();
        if (mounted) setAb604GeoJSON(data);
      } catch (error) {
        console.log('Failed to load AB-604 boundaries:', error);
      }
    };

    loadAb604();
    return () => {
      mounted = false;
    };
  }, []);

  function wordFreq(text) {
    const words = text.replace(/\./g, "").split(/\s/);
    const filteredWords = removeStopwords(words);
    const freqMap = {};

    for (const w of filteredWords) {
      const wlc = w.toLowerCase();
      if (!wlc) continue;
      freqMap[wlc] = (freqMap[wlc] || 0) + 1;
    }

    return Object.keys(freqMap).map(word => ({
      text: word,
      value: freqMap[word]
    }));
  }

  const selectedPostLength = props.selectedMapPosts.length;
  const filteredPostLength = filteredSelectedMapPosts.length;
  const selectedPostText = filteredPostLength === 1 ? "Post" : "Posts";

  useEffect(() => {
    if (!showIssueCategories) return;
    if (!activeIssueCategory) return;
    const exists = issueCategoryCounts.some(
      (entry) => entry.label.toLowerCase() === activeIssueCategory
    );
    if (!exists) setActiveIssueCategory('');
  }, [showIssueCategories, activeIssueCategory, issueCategoryCounts]);

  useEffect(() => {
    if (props.selectedMapPosts.length > 0) {
      const { allText, solutionText } = props.selectedMapPosts.reduce((acc, post) => {
        acc.allText += `${(post.properties.body || "").toLowerCase()} `;

        if (post.properties.solutions && Array.isArray(post.properties.solutions)) {
          post.properties.solutions.forEach(sol => {
            acc.solutionText += `${sol.solution || ""} `;
          });
        } else if (post.properties.solutions) {
          acc.solutionText += `${post.properties.solutions.solution || ""} `;
        }

        acc.solutionText += `${post.properties.solution || ""} `;
        return acc;
      }, { allText: '', solutionText: '' });

      setSolutionWordsForCloud(wordFreq(solutionText.trim()));
      setWordsForCloud(wordFreq(allText.trim()));
    } else {
      props.setIsPostPaneOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedMapPosts, props.mainPostData]);

  return (
    <div className="pc-selectedPosts">
      <div className="pc-selectedPosts__header">
        <div className="pc-selectedPosts__title text-black">
          {filteredPostLength} {selectedPostText}
          {activeIssueCategory && (
            <span className="text-xs ml-2">
              (filtered from {selectedPostLength})
            </span>
          )}
        </div>
      </div>

      <div className="flex justify-center">
        <button
          className="btn-active mb-2 pc-selectedPosts__toggle"
          onClick={() => {
            const next = !showIssueCategories;
            setShowIssueCategories(next);
            if (!next) setActiveIssueCategory('');
          }}
          type="button"
        >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-3 w-3 mr-1">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
        </svg>

          {showIssueCategories
            ? "Hide Issue Categories"
            : "Show Issue Categories"}
        </button>
      </div>

      {showIssueCategories && (
        <div className="pc-selectedPosts__charts">
          {issueCategoryCounts.length > 0 ? (
            <div style={{ height: '50vh' }}>
              <Bar data={issueCategoryChartData} options={issueCategoryChartOptions} onClick={handleIssueBarClick} />
            </div>
          ) : (
            <div className="text-xs">No issue category data available for selected posts.</div>
          )}
          {activeIssueCategory && (
            <div className="text-xs mt-2">
              Active category filter: <b>{activeIssueCategoryLabel}</b>{' '}
              <button type="button" className="btn-active" onClick={() => setActiveIssueCategory('')}>
                Clear
              </button>
            </div>
          )}
        </div>
      )}

        <div className="flex justify-center">
        <button
          className="btn-active pc-selectedPosts__toggle"
          onClick={() => setShowWordCloud(v => !v)}
          type="button"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3 mr-1">
            <path fill-rule="evenodd" d="M4.5 9.75a6 6 0 0 1 11.573-2.226 3.75 3.75 0 0 1 4.133 4.303A4.5 4.5 0 0 1 18 20.25H6.75a5.25 5.25 0 0 1-2.23-10.004 6.072 6.072 0 0 1-.02-.496Z" clip-rule="evenodd" />
          </svg>
          {showWordCloud ? "Hide Word Clouds" : "Show Word Clouds"}
        </button>
        </div>
      {showWordCloud && (
        <div id="word-cloud" className="pc-selectedPosts__wordcloudRow">
          <div className="flex">
            <div className="text-center text-xs font-semibold"><div>Reports</div><br/>
              <WordcloudBuilder key="issue-cloud" keyProp="issue-cloud" width={300} height={220} words={wordsForCloud} />
            <div className="mt-2">Solutions</div><br/>
              <WordcloudBuilder key="solution-cloud" keyProp="solution-cloud" width={300} height={220} words={solutionWordsForCloud} />
            </div>
          </div>
        </div>
      )}

      <div id="posts" className="pc-selectedPosts__list">
        {filteredSelectedMapPosts.map((post) => (
          <PostInstance
            key={`post-${post.properties.id}`}
            post={post}
            filterData={props.filterData}
            setSearch={props.setSearch}
            searchText={props.searchText}
            setSearchText={props.setSearchText}
            search={props.search}
            searchMade={props.searchMade}
            setSearchMade={props.setSearchMade}
            setEditUser={props.setEditUser}
            setEditPostId={props.setEditPostId}
            setEditPostBody={props.setEditPostBody}
            setIsVisiblePaneOpen={props.setIsVisiblePaneOpen}
            setIsEditMode={props.setIsEditMode}
            setIsPostPaneOpen={props.setIsPostPaneOpen}
            setIsLoginPaneOpen={props.setIsLoginPaneOpen}
            setIsRegisterPaneOpen={props.setIsRegisterPaneOpen}
            setIsSelectedPaneOpen={props.setIsSelectedPaneOpen}
            setSelectedMapPosts={props.setSelectedMapPosts}
            setPostGeoJSON={props.setPostGeoJSON}
            setViewport={props.setViewport}
            viewport={props.viewport}
            currentUser={props.currentUser}
            ab604GeoJSON={ab604GeoJSON}
          />
        ))}
      </div>
    </div>
  );
};

export default SelectedPosts;
